/**
 * Parses hex (#rgb / #rrggbb / #rrggbbaa) or rgb/rgba() into [r, g, b] in 0–255.
 * For 8-digit hex, RGB is used; alpha is ignored (contrast on opaque brand fills).
 * @param {string} input
 * @returns {[number, number, number]|null}
 */
export const parseRgbTriplet = (input) => {
  if (!input || typeof input !== "string") return null;
  const s = input.trim();
  if (!s || s === "transparent") return null;

  const hex = /^#([0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i.exec(s);
  if (hex) {
    let h = hex[1];
    if (h.length === 3) {
      h = h
        .split("")
        .map((c) => c + c)
        .join("");
    } else if (h.length === 8) {
      h = h.slice(0, 6);
    }

    return [
      parseInt(h.slice(0, 2), 16),
      parseInt(h.slice(2, 4), 16),
      parseInt(h.slice(4, 6), 16),
    ];
  }

  const rgb = /^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i.exec(s);
  if (rgb) return [+rgb[1], +rgb[2], +rgb[3]];

  return null;
};

/**
 * WCAG 2.1 relative luminance for sRGB (opaque).
 * @param {[number, number, number]} rgb
 * @returns {number}
 */
export const relativeLuminance = (rgb) => {
  const [r, g, b] = rgb.map((c) => {
    const x = c / 255;

    return x <= 0.03928 ? x / 12.92 : ((x + 0.055) / 1.055) ** 2.4;
  });

  return (0.2126 * r) + (0.7152 * g) + (0.0722 * b);
};

const LIGHT_BG_FOREGROUND = "#242533";
const DARK_BG_FOREGROUND = "#ffffff";

/**
 * Picks a readable foreground (icon) color for an opaque background.
 * @param {[number, number, number]} rgb
 * @returns {string} hex color
 */
export const foregroundHexForBackgroundRgb = (rgb) => {
  const L = relativeLuminance(rgb);

  return L > 0.45 ? LIGHT_BG_FOREGROUND : DARK_BG_FOREGROUND;
};

/**
 * Reads resolved background from an element (e.g. after CSS variables apply).
 * @param {HTMLElement} el
 * @returns {[number, number, number]|null}
 */
export const resolvedBackgroundRgb = (el) => {
  if (!el || typeof getComputedStyle === "undefined") return null;

  const bg = getComputedStyle(el).backgroundColor;

  return parseRgbTriplet(bg);
};

/** Default when no theme vars are set on :root yet. */
export const DEFAULT_BRAND_FALLBACK_HEX = "#051274";

/**
 * Sets `--on-brand` on :root for text/icons on brand-colored surfaces.
 * Uses already-applied CSS vars from ThemeInit/App as source of truth.
 * Order: `--bg-primary` (branch), then `--primary` (portal), then fallback hex.
 */
export const updateOnBrandCssVariables = () => {
  if (typeof document === "undefined") return;

  const root = document.documentElement;
  const cs = getComputedStyle(root);
  const raw =
    cs.getPropertyValue("--bg-primary").trim() ||
    cs.getPropertyValue("--primary").trim();
  let rgb = parseRgbTriplet(raw);

  if (!rgb) rgb = parseRgbTriplet(DEFAULT_BRAND_FALLBACK_HEX);
  if (!rgb) return;
  root.style.setProperty("--on-brand", foregroundHexForBackgroundRgb(rgb));
};
